import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStatus } from '../hooks/useAuthStatus';
import { deckService } from '../services/deckService';
import {
  Deck,
  DeckShareLink,
  DeckAnalytics,
  getFileTypeInfo,
  formatFileSize,
  formatDate,
  formatDateTime,
  formatDuration,
  getShareUrl,
} from '../types/deck';
import {
  FiArrowLeft,
  FiShare2,
  FiDownload,
  FiEye,
  FiUsers,
  FiClock,
  FiLink,
  FiCopy,
  FiCheck,
  FiTrash2,
  FiBell,
} from 'react-icons/fi';

import ShareLinkModal from '../components/decks/ShareLinkModal';
import DeleteConfirmModal from '../components/decks/DeleteConfirmModal';

// Type cast icons for React 18 compatibility
const ArrowLeftIcon = FiArrowLeft as React.FC<React.SVGProps<SVGSVGElement>>;
const ShareIcon = FiShare2 as React.FC<React.SVGProps<SVGSVGElement>>;
const DownloadIcon = FiDownload as React.FC<React.SVGProps<SVGSVGElement>>;
const EyeIcon = FiEye as React.FC<React.SVGProps<SVGSVGElement>>;
const UsersIcon = FiUsers as React.FC<React.SVGProps<SVGSVGElement>>;
const ClockIcon = FiClock as React.FC<React.SVGProps<SVGSVGElement>>;
const LinkIcon = FiLink as React.FC<React.SVGProps<SVGSVGElement>>;
const CopyIcon = FiCopy as React.FC<React.SVGProps<SVGSVGElement>>;
const CheckIcon = FiCheck as React.FC<React.SVGProps<SVGSVGElement>>;
const TrashIcon = FiTrash2 as React.FC<React.SVGProps<SVGSVGElement>>;
const BellIcon = FiBell as React.FC<React.SVGProps<SVGSVGElement>>;

const DeckDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isLoading: authLoading, error: authError, canAccessApp } = useAuthStatus();

  // Data state
  const [deck, setDeck] = useState<Deck | null>(null);
  const [shareLinks, setShareLinks] = useState<DeckShareLink[]>([]);
  const [analytics, setAnalytics] = useState<DeckAnalytics | null>(null);

  // Loading and error state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Load deck data
  const loadDeck = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const [deckData, linksData, analyticsData] = await Promise.all([
        deckService.getDeck(id),
        deckService.getShareLinks(id),
        deckService.getAnalytics(id),
      ]);
      setDeck(deckData);
      setShareLinks(linksData);
      setAnalytics(analyticsData);
    } catch (err: any) {
      console.error('Failed to load deck:', err);
      setError(err?.message || 'Failed to load deck');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (authLoading || authError || !canAccessApp) return;
    loadDeck();
  }, [authLoading, authError, canAccessApp, loadDeck]);

  // Handlers
  const handleBack = () => {
    navigate('/decks');
  };

  const handleDownload = async () => {
    if (!id) return;
    try {
      const url = await deckService.getDeckDownloadUrl(id);
      window.open(url, '_blank');
    } catch (err: any) {
      setError(err?.message || 'Failed to get download URL');
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await deckService.deleteDeck(id);
      navigate('/decks');
    } catch (err: any) {
      setError(err?.message || 'Failed to delete deck');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleCopyLink = async (shareToken: string, linkId: string) => {
    const url = getShareUrl(shareToken);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(linkId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Early returns
  if (authError) {
    return (
      <div className="p-4">
        <h1 className="text-xl font-bold mb-4 text-red-600">Authentication Error</h1>
        <p className="text-red-500">{authError}</p>
      </div>
    );
  }

  if (!canAccessApp) {
    return (
      <div className="p-4">
        <h1 className="text-xl font-bold mb-4">Access Denied</h1>
        <p>You don&apos;t have access to this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="su-deck-detail">
        <div className="su-deck-detail-header">
          <div className="su-deck-detail-title">
            <button className="su-deck-back-btn" onClick={handleBack}>
              <ArrowLeftIcon />
            </button>
            <div>
              <div className="h-7 w-48 bg-gray-800 rounded animate-pulse" />
              <div className="h-4 w-24 bg-gray-800 rounded animate-pulse mt-2" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!deck) {
    return (
      <div className="p-4">
        <h1 className="text-xl font-bold mb-4">Deck Not Found</h1>
        <p>The requested deck could not be found.</p>
        <button className="su-btn-secondary mt-4" onClick={handleBack}>
          Back to Decks
        </button>
      </div>
    );
  }

  const fileTypeInfo = getFileTypeInfo(deck.file_type);

  return (
    <>
      <div className="su-deck-detail">
        {/* Header */}
        <div className="su-deck-detail-header">
          <div className="su-deck-detail-title">
            <button className="su-deck-back-btn" onClick={handleBack}>
              <ArrowLeftIcon />
            </button>
            <div>
              <div className="su-deck-detail-name-row">
                <span
                  className="su-deck-file-badge"
                  style={{ backgroundColor: fileTypeInfo.color }}
                >
                  {fileTypeInfo.label}
                </span>
                <h1 className="su-deck-name">{deck.name}</h1>
              </div>
              <p className="su-deck-subtitle">
                {formatFileSize(deck.file_size)} • Uploaded {formatDate(deck.created_at)}
              </p>
            </div>
          </div>
          <div className="su-deck-actions">
            <button className="su-btn-secondary" onClick={handleDownload}>
              <DownloadIcon />
              <span>Download</span>
            </button>
            <button className="su-btn-secondary" onClick={() => setShowShareModal(true)}>
              <ShareIcon />
              <span>Share</span>
            </button>
            <button className="su-btn-danger-outline" onClick={() => setShowDeleteModal(true)}>
              <TrashIcon />
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="su-deck-error">
            {error}
            <button
              className="su-btn-secondary"
              style={{ marginLeft: '16px' }}
              onClick={() => setError(null)}
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Content */}
        <div className="su-deck-detail-content">
          {/* Analytics Section */}
          <div className="su-deck-section">
            <h2 className="su-deck-section-title">
              <EyeIcon />
              Analytics
            </h2>
            <div className="su-deck-analytics-grid">
              <div className="su-deck-analytics-card">
                <EyeIcon className="su-deck-analytics-icon" />
                <div className="su-deck-analytics-value">{analytics?.total_views || 0}</div>
                <div className="su-deck-analytics-label">Total Views</div>
              </div>
              <div className="su-deck-analytics-card">
                <UsersIcon className="su-deck-analytics-icon" />
                <div className="su-deck-analytics-value">{analytics?.unique_viewers || 0}</div>
                <div className="su-deck-analytics-label">Unique Viewers</div>
              </div>
              <div className="su-deck-analytics-card">
                <ClockIcon className="su-deck-analytics-icon" />
                <div className="su-deck-analytics-value">
                  {analytics?.average_duration ? formatDuration(analytics.average_duration) : '0s'}
                </div>
                <div className="su-deck-analytics-label">Avg. View Time</div>
              </div>
              <div className="su-deck-analytics-card">
                <LinkIcon className="su-deck-analytics-icon" />
                <div className="su-deck-analytics-value">{shareLinks.filter(l => l.is_active).length}</div>
                <div className="su-deck-analytics-label">Active Links</div>
              </div>
            </div>
          </div>

          {/* Share Links Section */}
          <div className="su-deck-section">
            <div className="su-deck-section-header">
              <h2 className="su-deck-section-title">
                <LinkIcon />
                Share Links
              </h2>
              <button
                className="su-deck-section-action"
                onClick={() => setShowShareModal(true)}
              >
                Manage Links
              </button>
            </div>
            {shareLinks.length === 0 ? (
              <div className="su-deck-empty-section">
                <LinkIcon style={{ width: 24, height: 24, opacity: 0.5, marginBottom: 8 }} />
                <p>No share links yet. Create one to share this deck.</p>
              </div>
            ) : (
              <div className="su-deck-share-links-compact">
                {shareLinks.slice(0, 3).map((link) => (
                  <div key={link.id} className="su-deck-share-link-compact">
                    <div className="su-deck-share-link-compact-info">
                      <span className="su-deck-share-link-compact-url">
                        {getShareUrl(link.share_token)}
                      </span>
                      <span className={link.is_active ? 'su-badge-active' : 'su-badge-draft'}>
                        {link.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {link.email_notifications && (
                        <span className="su-deck-notification-badge">
                          <BellIcon width={12} height={12} />
                        </span>
                      )}
                    </div>
                    <button
                      className="su-deck-copy-btn"
                      onClick={() => handleCopyLink(link.share_token, link.id)}
                    >
                      {copiedId === link.id ? (
                        <CheckIcon width={14} height={14} style={{ color: '#22c55e' }} />
                      ) : (
                        <CopyIcon width={14} height={14} />
                      )}
                    </button>
                  </div>
                ))}
                {shareLinks.length > 3 && (
                  <button
                    className="su-deck-view-all-btn"
                    onClick={() => setShowShareModal(true)}
                  >
                    View all {shareLinks.length} links
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Recent Views Section */}
          <div className="su-deck-section">
            <h2 className="su-deck-section-title">
              <EyeIcon />
              Recent Views
            </h2>
            {analytics?.recent_views && analytics.recent_views.length > 0 ? (
              <div className="su-deck-views-table">
                <div className="su-deck-views-header">
                  <span>Viewer</span>
                  <span>Date</span>
                  <span>Duration</span>
                </div>
                {analytics.recent_views.slice(0, 10).map((view) => (
                  <div key={view.id} className="su-deck-views-row">
                    <span className="su-deck-view-email">
                      {view.viewer_email || 'Anonymous'}
                    </span>
                    <span className="su-deck-view-date">
                      {formatDateTime(view.viewed_at)}
                    </span>
                    <span className="su-deck-view-duration">
                      {formatDuration(view.duration_seconds)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="su-deck-empty-section">
                <EyeIcon style={{ width: 24, height: 24, opacity: 0.5, marginBottom: 8 }} />
                <p>No views yet. Share your deck to start tracking engagement.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showShareModal && deck && (
        <ShareLinkModal
          deck={{ ...deck, view_count: analytics?.total_views || 0, share_link_count: shareLinks.length }}
          onClose={() => {
            setShowShareModal(false);
            loadDeck(); // Refresh share links
          }}
        />
      )}

      {showDeleteModal && (
        <DeleteConfirmModal
          title="Delete Deck"
          message={`Are you sure you want to delete "${deck.name}"? This will permanently delete the deck, all share links, and analytics. This action cannot be undone.`}
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          isDeleting={isDeleting}
        />
      )}
    </>
  );
};

export default DeckDetail;
